import { z } from "zod";
import { MediaType } from "../enums";
import { RouteMap } from "./customerReporting";
import { deductionSchema } from "./deductions";
import { reportsShiftSchema } from "./shift";

const paystubReportingPeriod = z.object({
  start: z.string(),
  end: z.string(),
  pay_schedule: z.string(),
});

const paystubTaxSchema = z.object({ name: z.string(), amount: z.string() });

const ytdDeductionsSchema = z
  .object({
    total: z.string(),
  })
  .and(z.record(z.string(), z.string()));

const paystubYtdsSchema = z.object({
  gross_pay: z.string(),
  employee_taxes: z.string(),
  net_pay: z.string(),
  employee_deductions: z.string(),
  deductions: ytdDeductionsSchema,
  withholdings: z.record(z.string(), z.string()),
  cash_tips: z.string(),
  two_percent: z.string(),
  // Emitted only when the Paystubs Redesign flag is ON (paired with the
  // top-level `reimbursements` block). Optional keeps the legacy wire shape
  // unchanged.
  reimbursements: z.string().optional(),
});

const paystubTotalsSchema = z.object({
  gross_earnings: z.string(),
  gross_pay: z.string(),
  net_pay: z.string(),
  employee_garnishments: z.string(),
  company_debit: z.string(),
  company_cash_requirement: z.string(),
  employee_deductions: z.string(),
  employer_deductions: z.string(),
  employee_taxes: z.string(),
  employer_taxes: z.string(),
  cash_tips: z.string(),
  two_percent_shareholder_healthcare_reimbursement: z.string(),
  // The following fields are only populated when the Paystubs Redesign flag
  // is ON. They are optional so the legacy wire shape is unchanged.
  // Current values are written by la-forge; *_ytd values are written by pike
  // (the only layer with cross-check YTD aggregation).
  total_hours_worked: z.string().optional(),
  total_reimbursements_current: z.string().optional(),
  total_reimbursements_ytd: z.string().optional(),
  check_amount_current: z.string().optional(),
  check_amount_ytd: z.string().optional(),
});

const paystubDeductionsSchema = z.object({
  deduction_type: z.string(),
  base_deduction_type: z.string(),
  value: z.string(),
  // Optional — emitted only when the Paystubs Redesign flag is ON.
  // Legacy (flag OFF) responses and historical snapshots omit this field
  // and the wire shape stays exactly as it was pre-redesign.
  pre_tax: z.boolean().optional(),
});

const paystubEmployerContributionSchema = z.object({
  name: z.string(),
  type: z.string(),
  current: z.string(),
  ytd: z.string(),
});

const paystubEarningsRowSchema = z.object({
  hours: z.string(),
  rate: z.string(),
  current: z.string(),
});

// Aggregated, cross-location earnings rows surfaced on the "Earnings" table.
// Only OT / DT / Regular are populated in this release (see paystub PRD).
const paystubEarningsSchema = z.object({
  regular: paystubEarningsRowSchema.nullable(),
  overtime: paystubEarningsRowSchema.nullable(),
  doubletime: paystubEarningsRowSchema.nullable(),
});

const paystubReimbursementItemSchema = z.object({
  description: z.string(),
  current: z.string(),
});

const paystubReimbursementsSchema = z.object({
  items: z.array(paystubReimbursementItemSchema),
  total_current: z.string(),
  total_ytd: z.string(),
});

// Structured per-row earnings table for the redesigned Pay Statement Page 1.
// Each item is a single visible row in the Earnings table. `hours_or_units`
// and `rate` are optional because dollar-only earnings (bonus, commission,
// charged tips) have neither, while hour-based earnings (Regular, OT, DT,
// PTO, Sick, etc.) include both. `ytd` is optional in Phase 1 because pike
// does not yet aggregate per-earnings-type YTD across the year's checks —
// renderers must show an em-dash when missing.
const paystubEarningsItemSchema = z.object({
  description: z.string(),
  // Some historical payloads/snapshots serialize missing optional values as
  // explicit `null` instead of omitting the key. Accept both to keep report
  // generation resilient while the renderer still treats nullish as em-dash.
  hours_or_units: z.string().nullable().optional(),
  rate: z.string().nullable().optional(),
  current: z.string(),
  ytd: z.string().nullable().optional(),
});

const paystubEarningsSummarySchema = z.object({
  items: z.array(paystubEarningsItemSchema),
  total_current: z.string(),
  total_ytd: z.string().optional(),
});

const paystubShiftCorrectionSchema = z.object({
  type: z.string(),
  rate: z.string(),
  earned: z.string(),
});

export const paystubSchema = z.object({
  employeeID: z.string(),
  full_name: z.string(),
  address: z.string(),
  ssn: z.string(),
  city_state_address: z.string(),
  has_employee_checks: z.boolean(),
  shifts: z.record(z.string(), z.array(reportsShiftSchema)).nullable(),
  reporting_period: paystubReportingPeriod,
  ytds: paystubYtdsSchema,
  check_date: z.string(),
  total_employee_deductions: z.string(),
  deductions: z.array(paystubDeductionsSchema),
  // The following six fields are emitted only when the Paystubs Redesign
  // flag is ON. They are `.optional()` (not `.default()`) so legacy responses
  // and historical snapshot baselines stay byte-identical to today's wire
  // shape — no new keys appear unless the flag has actually enabled them.
  employer_contributions: z.array(paystubEmployerContributionSchema).optional(),
  pre_tax_deductions_total: z
    .object({ current: z.string(), ytd: z.string() })
    .optional(),
  post_tax_deductions_total: z
    .object({ current: z.string(), ytd: z.string() })
    .optional(),
  employer_contributions_total: z
    .object({ current: z.string(), ytd: z.string() })
    .optional(),
  net_pay: z.string(),
  gross_pay: z.string(),
  total_employee_taxes: z.string(),
  totals: paystubTotalsSchema,
  taxes: z.array(paystubTaxSchema),
  // Employer-paid taxes (FUTA, employer Social Security/Medicare, SUTA, etc.).
  // Emitted only when the Paystubs Redesign flag is ON; mirrors `taxes` but
  // sourced from `Withholding.paidBy === "EMPLOYER_LIABILITY"`.
  employer_taxes: z.array(paystubTaxSchema).optional(),
  // Free-form per-check message surfaced from `EmployeeCheck.notes`. Emitted
  // only when the Paystubs Redesign flag is ON and the underlying note is
  // non-empty. Renderers must hide the section when missing.
  employer_message: z.string().optional(),
  disbursement: z.string(),
  earnings: paystubEarningsSchema.optional(),
  // Structured per-row earnings table used by the redesigned Pay Statement
  // Page 1 renderer. Emitted only when the Paystubs Redesign flag is ON.
  // Pike uses the presence of this field as the "render V2" signal.
  earnings_summary: paystubEarningsSummarySchema.optional(),
  reimbursements: paystubReimbursementsSchema.optional(),
  accrualPoliciesWithinReportingPeriod: z.array(
    z.object({
      policyType: z.string(),
      balance: z.number(),
      accrued: z.number(),
      used: z.number().nullable(),
      usedYTD: z.number(),
      accruedYTD: z.number(),
    })
  ),
  corrections: z.array(paystubShiftCorrectionSchema).nullable(),
  hasOregonWithholding: z.boolean().default(false).optional(),
  oregonBIN: z.string().nullable().optional(),
});

export const paystubSchemaArray = z.array(paystubSchema);

export type PaystubResponse = z.infer<typeof paystubSchema>;
export type PaystubMappingResponse = {
  kind: RouteMap.paystub;
  media_type: MediaType.Pdf;
  paystubs: { [check_date: string]: PaystubResponse[] };
};
export type PaystubShape = {
  media_type: MediaType.Pdf;
  data: Buffer;
};

export type PaystubYTD = z.infer<typeof paystubYtdsSchema>;
export type EmployeeIDPaystubMapping = {
  quark_ids: string[];
  la_forge_ids: string[];
  quark_mapping: Record<string, string>;
};
export type PaystubDeductions = {
  readonly employeeID: string;
  readonly taxengine_check_id: string;
  readonly deductions: {
    name: string;
    employee_contribution_amount: number;
    employer_contribution_amount: number;
  }[];
  readonly total_deductions: {
    readonly employee_contribution_amount: number;
    readonly employer_contribution_amount: number;
  };
};
export type PaystubDeductionsMap = Record<
  string,
  Omit<PaystubDeductions, "employeeID">
>;

const paystubWithholdingsSchema = z.object({
  amount: z.number(),
  taxType: z.string(),
  name: z.string(),
  payer: z.enum(["EE", "ER"]),
});

const paystubCheckSchema = z.object({
  id: z.string(),
  pay_date: z.string(),
  employeeID: z.string(),
  earnings: z.object({
    amount: z.number(),
    location: z.object({
      street1: z.string(),
      street2: z.string(),
      city: z.string(),
      state: z.string(),
      zip: z.string(),
    }),
    payType: z.string(),
    name: z.string(),
  }),
  withholdings: paystubWithholdingsSchema,
  deductions: z.array(deductionSchema),
});

const paystubCheckAccrualsSchema = z.object({
  companyID: z.string().optional(),
  checks: paystubCheckSchema.optional(),
  name: z.string().optional(),
  message: z.string().optional(),
});

export type PaystubCheckAccruals = z.infer<typeof paystubCheckAccrualsSchema>;
export type PaystubWithholdings = z.infer<typeof paystubWithholdingsSchema>;
